import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { ClientApiError } from "../auth/api";
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Input,
  Select,
  Skeleton,
  Textarea,
  Toast,
} from "../components/ui";
import { DogAutocomplete } from "../dogs/DogAutocomplete";
import type { DogSummary } from "../dogs/api";
import {
  cancelPedigreeRequest,
  createPedigreeRequest,
  getMemberPedigree,
  getPublicPedigree,
  listPedigreeModerationQueue,
  pedigreeSourceDocumentUrl,
  reviewPedigreeRequest,
  uploadPedigreeSourceDocument,
  type ParentType,
  type PedigreeChangeRequest,
  type PedigreeDocument,
} from "../pedigree/api";
import { PedigreeExplorer, type GenerationCount } from "../pedigree/PedigreeTree";

function errorFrom(caught: unknown): { message: string; requestId?: string; status?: number } {
  if (caught instanceof ClientApiError) {
    return {
      message: caught.problem.message,
      requestId: caught.problem.requestId,
      status: caught.status,
    };
  }
  return { message: "Không thể xử lý yêu cầu. Vui lòng thử lại." };
}

function generationFrom(value: string | null): GenerationCount {
  return value === "4" ? 4 : value === "5" ? 5 : 3;
}

function sexLabel(value: "male" | "female" | "unknown"): string {
  return value === "male" ? "Đực" : value === "female" ? "Cái" : "Chưa rõ";
}

function parentLabel(value: ParentType | null): string {
  return value === "sire" ? "Cha" : value === "dam" ? "Mẹ" : "Hồ sơ gốc";
}

function sourceLabel(value: string): string {
  const labels: Record<string, string> = {
    owner_statement: "Xác nhận của người quản lý",
    registry_document: "Tài liệu đăng ký",
    verified_database: "Cơ sở dữ liệu đã xác minh",
    import: "Import có kiểm soát",
    moderator: "Moderator ghi nhận",
    legacy: "Dữ liệu kế thừa",
  };
  return labels[value] ?? value;
}

function warningLabel(value: string): string {
  if (value === "parent_age_under_9_months") return "Cha/mẹ dưới 9 tháng khi con sinh";
  if (value === "parent_age_over_20_years") return "Cha/mẹ trên 20 tuổi khi con sinh";
  return value;
}

export function PublicPedigreePage() {
  const { slug = "" } = useParams();
  const { principal } = useAuth();
  const [search, setSearch] = useSearchParams();
  const generations = generationFrom(search.get("generations"));
  const [pedigree, setPedigree] = useState<PedigreeDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; requestId?: string } | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getPublicPedigree(slug, generations)
      .then((result) => {
        if (!active) return;
        setPedigree(result.pedigree);
        setError(null);
      })
      .catch((caught) => {
        if (active) setError(errorFrom(caught));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [generations, slug]);

  const root = pedigree?.nodes.find((node) => node.generation === 0)?.dog;
  return (
    <main className="page-container pedigree-page" data-testid="public-pedigree-page">
      <header className="page-heading pedigree-heading">
        <div>
          <p className="eyebrow">WorkingDog VN · Pedigree</p>
          <h1>{root ? `Phả hệ ${root.registeredName}` : "Phả hệ chó làm việc"}</h1>
          <p>Cây phả hệ có nguồn, ảnh, trạng thái xác minh và lịch sử duyệt; dữ liệu chưa duyệt không được công khai.</p>
        </div>
        {principal && pedigree ? (
          <Link
            className="ui-button ui-button--secondary ui-button--md"
            to={`/member/dogs/${pedigree.rootDogId}/pedigree`}
          >
            Quản lý phả hệ
          </Link>
        ) : null}
      </header>
      {loading ? <Skeleton lines={10} /> : null}
      {!loading && error ? (
        <ErrorState
          title="Không tải được phả hệ"
          description={error.message}
          requestId={error.requestId}
        />
      ) : null}
      {!loading && !error && pedigree ? (
        <PedigreeExplorer
          pedigree={pedigree}
          onGenerationChange={(value) => setSearch({ generations: String(value) })}
        />
      ) : null}
    </main>
  );
}

function RequestForm({
  dogId,
  dogVersion,
  initialType,
  onCreated,
  onClose,
}: {
  dogId: string;
  dogVersion: number;
  initialType: ParentType;
  onCreated: () => void;
  onClose: () => void;
}) {
  const [type, setType] = useState<ParentType>(initialType);
  const [operation, setOperation] = useState<"set" | "remove">("set");
  const [parent, setParent] = useState<DogSummary | null>(null);
  const [source, setSource] = useState<
    "owner_statement" | "registry_document" | "verified_database"
  >("owner_statement");
  const [summary, setSummary] = useState("");
  const [url, setUrl] = useState("");
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<{ message: string; requestId?: string } | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (source === "registry_document" && !documentFile) {
        setError({ message: "Nguồn tài liệu đăng ký bắt buộc phải có ảnh hoặc PDF để admin đối chiếu." });
        return;
      }
      if (documentFile && documentFile.size > 8 * 1024 * 1024) {
        setError({ message: "Ảnh hoặc PDF phả hệ không được vượt quá 8 MB." });
        return;
      }
      const uploaded = documentFile
        ? await uploadPedigreeSourceDocument(dogId, documentFile)
        : null;
      await createPedigreeRequest(dogId, {
        relationshipType: type,
        proposedParentDogId: operation === "remove" ? null : parent?.id ?? null,
        sourceType: source,
        sourceSummary: summary,
        sourceUrl: url || null,
        sourceDocumentId: uploaded?.document.id ?? null,
        expectedDogVersion: dogVersion,
      });
      onCreated();
    } catch (caught) {
      setError(errorFrom(caught));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="pedigree-request-form" onSubmit={(event) => void submit(event)}>
      <header>
        <div>
          <p className="eyebrow">Người dùng gửi · Admin duyệt</p>
          <h3>Đề xuất thay đổi cha/mẹ</h3>
        </div>
        <Button type="button" size="sm" variant="ghost" onClick={onClose}>
          Đóng
        </Button>
      </header>
      {error ? (
        <ErrorState
          title="Không gửi được request"
          description={error.message}
          requestId={error.requestId}
        />
      ) : null}
      <div className="form-grid">
        <Select
          label="Quan hệ"
          value={type}
          onChange={(event) => {
            setType(event.target.value as ParentType);
            setParent(null);
          }}
        >
          <option value="sire">Cha (sire)</option>
          <option value="dam">Mẹ (dam)</option>
        </Select>
        <Select
          label="Thao tác"
          value={operation}
          onChange={(event) => setOperation(event.target.value as "set" | "remove")}
        >
          <option value="set">Gắn hoặc thay cha/mẹ</option>
          <option value="remove">Gỡ quan hệ hiện tại</option>
        </Select>
        {operation === "set" ? (
          <DogAutocomplete
            label={type === "sire" ? "Hồ sơ chó cha" : "Hồ sơ chó mẹ"}
            expectedSex={type === "sire" ? "male" : "female"}
            value={parent}
            onSelect={setParent}
          />
        ) : null}
        <Select
          label="Loại nguồn"
          value={source}
          onChange={(event) => setSource(event.target.value as typeof source)}
        >
          <option value="owner_statement">Xác nhận người quản lý</option>
          <option value="registry_document">Ảnh / giấy phả hệ, giấy đăng ký</option>
          <option value="verified_database">Cơ sở dữ liệu đã xác minh</option>
        </Select>
        <Input
          label="URL nguồn HTTPS (tùy chọn)"
          type="url"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
        />
        <label className="pedigree-upload-field">
          <span>
            Ảnh / PDF phả hệ {source === "registry_document" ? "(bắt buộc)" : "(tùy chọn)"}
          </span>
          <small>Nhận PDF, JPG, PNG hoặc WebP, tối đa 8 MB. File chỉ người gửi và admin/moderator được xem trước khi duyệt.</small>
          <input
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/webp"
            required={source === "registry_document"}
            onChange={(event) => setDocumentFile(event.target.files?.[0] ?? null)}
          />
          {documentFile ? (
            <strong>
              {documentFile.name} · {(documentFile.size / 1024 / 1024).toFixed(2)} MB
            </strong>
          ) : null}
        </label>
      </div>
      <Textarea
        label="Mô tả nguồn và lý do"
        hint="Từ 20 đến 4.000 ký tự. Cây công khai không đổi trước khi moderator duyệt."
        value={summary}
        onChange={(event) => setSummary(event.target.value)}
        minLength={20}
        maxLength={4000}
        required
      />
      <Button type="submit" disabled={saving || (operation === "set" && !parent)}>
        {saving ? "Đang tải tài liệu và gửi…" : "Gửi vào hàng đợi"}
      </Button>
    </form>
  );
}

export function MemberPedigreePage() {
  const { id = "" } = useParams();
  const [generations, setGenerations] = useState<GenerationCount>(3);
  const [pedigree, setPedigree] = useState<PedigreeDocument | null>(null);
  const [requests, setRequests] = useState<PedigreeChangeRequest[]>([]);
  const [canRequest, setCanRequest] = useState(false);
  const [formType, setFormType] = useState<ParentType | null>(null);
  const [reload, setReload] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<
    { message: string; requestId?: string; status?: number } | null
  >(null);
  const [toast, setToast] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getMemberPedigree(id, generations)
      .then((result) => {
        if (!active) return;
        setPedigree(result.pedigree);
        setRequests(result.requests);
        setCanRequest(result.viewer.canRequestChange);
        setError(null);
      })
      .catch((caught) => {
        if (active) setError(errorFrom(caught));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [generations, id, reload]);

  async function cancel(requestId: string) {
    try {
      await cancelPedigreeRequest(requestId);
      setReload((value) => value + 1);
    } catch (caught) {
      setError(errorFrom(caught));
    }
  }

  const root = pedigree?.nodes.find((node) => node.generation === 0)?.dog;
  if (loading) return <Skeleton lines={10} />;
  if (error && !pedigree) {
    return (
      <ErrorState
        title={error.status === 403 ? "Không có quyền xem phả hệ" : "Không tải được phả hệ"}
        description={error.message}
        requestId={error.requestId}
      />
    );
  }
  if (!pedigree) {
    return (
      <EmptyState
        title="Không có dữ liệu"
        description="Hồ sơ này chưa sẵn sàng cho Pedigree Engine."
      />
    );
  }

  return (
    <section className="pedigree-page" data-testid="member-pedigree-page">
      <header className="page-heading pedigree-heading">
        <div>
          <p className="eyebrow">Pedigree Engine · Member</p>
          <h2>{root?.registeredName ?? "Phả hệ"}</h2>
          <p>Người dùng gửi cha/mẹ và ảnh phả hệ; dữ liệu chỉ xuất hiện công khai sau khi admin duyệt.</p>
        </div>
        {canRequest ? <Button onClick={() => setFormType("sire")}>Đề xuất cha/mẹ</Button> : null}
      </header>
      {error ? (
        <ErrorState
          title="Có lỗi khi xử lý"
          description={error.message}
          requestId={error.requestId}
        />
      ) : null}
      {formType ? (
        <RequestForm
          dogId={pedigree.rootDogId}
          dogVersion={pedigree.rootVersion}
          initialType={formType}
          onClose={() => setFormType(null)}
          onCreated={() => {
            setFormType(null);
            setToast(true);
            setReload((value) => value + 1);
          }}
        />
      ) : null}
      <PedigreeExplorer
        pedigree={pedigree}
        onGenerationChange={setGenerations}
        canAdd={canRequest}
        onAdd={setFormType}
      />
      <section className="pedigree-request-list">
        <h3>Lịch sử request gần đây</h3>
        {requests.length === 0 ? (
          <p>Chưa có change request.</p>
        ) : (
          requests.map((request) => (
            <article key={request.id}>
              <div>
                <strong>
                  {parentLabel(request.relationshipType)} · {request.proposedParent?.registeredName ?? "Gỡ quan hệ"}
                </strong>
                <span>
                  {sourceLabel(request.sourceType)} · version gốc {request.baseDogVersion}
                </span>
                {request.sourceDocumentId ? (
                  <a
                    href={pedigreeSourceDocumentUrl(request.sourceDocumentId)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Xem ảnh / tài liệu đã gửi
                  </a>
                ) : null}
              </div>
              <Badge
                tone={
                  request.status === "approved"
                    ? "success"
                    : request.status === "rejected"
                      ? "danger"
                      : request.status === "pending"
                        ? "warning"
                        : "neutral"
                }
              >
                {request.status}
              </Badge>
              {request.status === "pending" ? (
                <Button size="sm" variant="ghost" onClick={() => void cancel(request.id)}>
                  Hủy request
                </Button>
              ) : null}
            </article>
          ))
        )}
      </section>
      {toast ? (
        <Toast
          title="Đã gửi"
          message="Change request đang chờ moderator xác minh nguồn."
          onClose={() => setToast(false)}
        />
      ) : null}
    </section>
  );
}

export function PedigreeModerationPage() {
  const [items, setItems] = useState<PedigreeChangeRequest[]>([]);
  const [selected, setSelected] = useState<PedigreeChangeRequest | null>(null);
  const [note, setNote] = useState("");
  const [timelineReason, setTimelineReason] = useState("");
  const [sexReason, setSexReason] = useState("");
  const [evidenceId, setEvidenceId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<{ message: string; requestId?: string } | null>(null);
  const [success, setSuccess] = useState(false);

  function load() {
    setLoading(true);
    listPedigreeModerationQueue()
      .then((result) => {
        setItems(result.requests);
        setError(null);
      })
      .catch((caught) => setError(errorFrom(caught)))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  async function review(action: "approve" | "reject") {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      await reviewPedigreeRequest(selected.id, {
        action,
        reviewerNote: note || null,
        timelineOverrideReason: timelineReason || null,
        sexOverrideReason: sexReason || null,
        overrideEvidenceDocumentId: evidenceId || null,
      });
      setSelected(null);
      setNote("");
      setTimelineReason("");
      setSexReason("");
      setEvidenceId("");
      setSuccess(true);
      load();
    } catch (caught) {
      setError(errorFrom(caught));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="pedigree-page" data-testid="pedigree-moderation-page">
      <header className="page-heading pedigree-heading">
        <div>
          <p className="eyebrow">Pedigree moderation</p>
          <h2>Kiểm duyệt quan hệ cha/mẹ</h2>
          <p>Admin đối chiếu ảnh/PDF, nguồn, timeline, giới tính và dog version trước khi publish.</p>
        </div>
      </header>
      {loading ? <Skeleton lines={7} /> : null}
      {error ? (
        <ErrorState
          title="Không xử lý được hàng đợi"
          description={error.message}
          requestId={error.requestId}
          action={{ label: "Tải lại", onClick: load }}
        />
      ) : null}
      {!loading && !error && items.length === 0 ? (
        <EmptyState title="Hàng đợi trống" description="Không có change request phả hệ đang chờ." />
      ) : null}
      <div className="pedigree-moderation-grid">
        <div className="pedigree-moderation-list">
          {items.map((request) => (
            <article key={request.id}>
              <div>
                <p className="eyebrow">
                  {parentLabel(request.relationshipType)} · dog version {request.baseDogVersion}
                </p>
                <h3>{request.dogName}</h3>
                <p>Đề xuất: {request.proposedParent?.registeredName ?? "Gỡ quan hệ"}</p>
                <p>{sourceLabel(request.sourceType)} · {request.sourceSummary}</p>
                {request.validationWarnings.map((warning) => (
                  <Badge key={warning} tone="warning">
                    Cảnh báo: {warningLabel(warning)}
                  </Badge>
                ))}
              </div>
              <Button size="sm" variant="secondary" onClick={() => setSelected(request)}>
                Mở review
              </Button>
            </article>
          ))}
        </div>
        {selected ? (
          <form
            className="pedigree-review-panel"
            onSubmit={(event) => {
              event.preventDefault();
              void review("approve");
            }}
          >
            <header>
              <div>
                <p className="eyebrow">Review request</p>
                <h3>{selected.dogName}</h3>
              </div>
              <Button type="button" size="sm" variant="ghost" onClick={() => setSelected(null)}>
                Đóng
              </Button>
            </header>
            <dl>
              <div><dt>Quan hệ</dt><dd>{parentLabel(selected.relationshipType)}</dd></div>
              <div><dt>Parent</dt><dd>{selected.proposedParent?.registeredName ?? "Gỡ quan hệ"}</dd></div>
              <div><dt>Sex parent</dt><dd>{selected.proposedParent ? sexLabel(selected.proposedParent.sex) : "—"}</dd></div>
              <div><dt>Nguồn</dt><dd>{sourceLabel(selected.sourceType)}</dd></div>
              <div><dt>Người gửi</dt><dd>{selected.requestedBy.displayName}</dd></div>
            </dl>
            <div className="pedigree-review-sources">
              {selected.sourceUrl ? (
                <a href={selected.sourceUrl} target="_blank" rel="noreferrer">Mở URL nguồn</a>
              ) : null}
              {selected.sourceDocumentId ? (
                <a
                  className="ui-button ui-button--secondary ui-button--sm"
                  href={pedigreeSourceDocumentUrl(selected.sourceDocumentId)}
                  target="_blank"
                  rel="noreferrer"
                >
                  Mở ảnh / PDF người dùng tải lên
                </a>
              ) : (
                <p>Request này không đính kèm ảnh hoặc tài liệu.</p>
              )}
            </div>
            <Textarea
              label="Reviewer note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
            {selected.validationWarnings.length > 0 ? (
              <>
                <Textarea
                  label="Lý do override timeline"
                  value={timelineReason}
                  onChange={(event) => setTimelineReason(event.target.value)}
                  minLength={20}
                />
                <Input
                  label="Verified evidence document ID"
                  value={evidenceId}
                  onChange={(event) => setEvidenceId(event.target.value)}
                />
              </>
            ) : null}
            {selected.proposedParent &&
            selected.proposedParent.sex !==
              (selected.relationshipType === "sire" ? "male" : "female") ? (
              <Textarea
                label="Lý do ngoại lệ giới tính lịch sử"
                value={sexReason}
                onChange={(event) => setSexReason(event.target.value)}
                minLength={20}
              />
            ) : null}
            <div className="button-row">
              <Button type="submit" disabled={saving}>Duyệt & tạo version</Button>
              <Button
                type="button"
                variant="danger"
                disabled={saving || note.trim().length < 10}
                onClick={() => void review("reject")}
              >
                Từ chối
              </Button>
            </div>
          </form>
        ) : null}
      </div>
      {success ? (
        <Toast
          title="Đã review"
          message="Quan hệ, history, dog version và audit đã được cập nhật atomically."
          onClose={() => setSuccess(false)}
        />
      ) : null}
    </section>
  );
}
